import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import config from './config';
import Dashboard from './components/Dashboard';
import CustomerLayout from './CustomerLayout';
import { setOrganizationContext } from './orgContext';

interface MyOrganization {
  membershipId: number;
  organizationId: number;
  organizationName: string;
  organizationSlug: string;
  isDefault: boolean;
  roles: string[];
}

interface DiscoverOrganizationItem {
  organizationId: number;
  name: string;
  slug: string;
}

interface MyJoinRequest {
  id: number;
  organizationId: number;
  organizationName: string;
  organizationSlug: string;
  requestedRoleKey: string;
  status: string;
  message?: string | null;
  createdAt: string;
  reviewedAt?: string | null;
}

const ROLE_OPTIONS = ['Student', 'Parent', 'Teacher', 'Principal'];
const ORGANIZATION_TYPE_OPTIONS = ['School', 'Tutoring center', 'Coaching institute', 'Independent educator'];
const TEAM_SIZE_OPTIONS = ['1-20', '21-50', '51-100', '100+'];

const hasAdminRole = (roles: string[]): boolean => {
  const normalizedRoles = roles
    .map(role => role.trim().toLowerCase())
    .filter(Boolean);

  return normalizedRoles.some(role => ['owner', 'principal', 'staff', 'admin'].includes(role));
};

const OrganizationPreDashboard: React.FC = () => {
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const apiBaseUrl = `${config.apiBaseUrl}/api/organizations`;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [organizations, setOrganizations] = useState<MyOrganization[]>([]);
  const [myJoinRequests, setMyJoinRequests] = useState<MyJoinRequest[]>([]);
  const [discoverQuery, setDiscoverQuery] = useState('');
  const [discoveredOrganizations, setDiscoveredOrganizations] = useState<DiscoverOrganizationItem[]>([]);
  const [showJoinPanel, setShowJoinPanel] = useState(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number>(0);
  const [requestedRoleKey, setRequestedRoleKey] = useState('Student');
  const [requestMessage, setRequestMessage] = useState('');
  const [showDashboard, setShowDashboard] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createStep, setCreateStep] = useState<'form' | 'review'>('form');
  const [newOrganizationName, setNewOrganizationName] = useState('');
  const [newOrganizationSlug, setNewOrganizationSlug] = useState('');
  const [newOrganizationDescription, setNewOrganizationDescription] = useState('');
  const [newOrganizationType, setNewOrganizationType] = useState('School');
  const [newOrganizationFocus, setNewOrganizationFocus] = useState('');
  const [newOrganizationSize, setNewOrganizationSize] = useState('1-20');

  const getManagementToken = useCallback(async (): Promise<string> => {
    const tokenResponse = await getAccessTokenSilently().catch(() =>
      getAccessTokenSilently({ cacheMode: 'off' } as any)
    );

    const token =
      typeof tokenResponse === 'string'
        ? tokenResponse
        : (tokenResponse as { access_token?: string })?.access_token;

    if (!token) {
      throw new Error('Unable to get access token. Please sign in again and retry.');
    }

    return token;
  }, [getAccessTokenSilently]);

  const withAuth = useCallback(
    async (request: AxiosRequestConfig) => {
      const token = await getManagementToken();
      return axios({
        ...request,
        headers: {
          ...(request.headers ?? {}),
          Authorization: `Bearer ${token}`
        }
      });
    },
    [getManagementToken]
  );

  const defaultOrganizationId = useMemo(
    () => organizations.find(o => o.isDefault)?.organizationId ?? organizations[0]?.organizationId ?? null,
    [organizations]
  );

  const forcePreView = useMemo(() => {
    const value = searchParams.get('pre');
    return value === '1' || value === 'true';
  }, [searchParams]);

  const slugify = useCallback((value: string) => {
    return value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }, []);

  const resetCreateModal = useCallback(() => {
    setShowCreateModal(false);
    setCreateStep('form');
    setNewOrganizationName('');
    setNewOrganizationSlug('');
    setNewOrganizationDescription('');
    setNewOrganizationType('School');
    setNewOrganizationFocus('');
    setNewOrganizationSize('1-20');
  }, []);

  const buildCreateDescription = useCallback(() => {
    const details = [
      newOrganizationDescription.trim(),
      newOrganizationType.trim() ? `Type: ${newOrganizationType.trim()}` : '',
      newOrganizationFocus.trim() ? `Focus: ${newOrganizationFocus.trim()}` : '',
      newOrganizationSize.trim() ? `Size: ${newOrganizationSize.trim()}` : ''
    ].filter(Boolean);

    return details.join(' | ');
  }, [newOrganizationDescription, newOrganizationFocus, newOrganizationSize, newOrganizationType]);

  const loadContextData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [myResponse, myRequestsResponse, discoverResponse] = await Promise.all([
        withAuth({ method: 'get', url: `${apiBaseUrl}/my` }),
        withAuth({ method: 'get', url: `${apiBaseUrl}/my-join-requests`, params: { status: 'Pending' } }).catch(() => ({ data: [] })),
        withAuth({ method: 'get', url: `${apiBaseUrl}/discover` }).catch(() => ({ data: [] }))
      ]);

      const myOrganizations = (myResponse.data ?? []) as MyOrganization[];
      setOrganizations(myOrganizations);
      setMyJoinRequests((myRequestsResponse.data ?? []) as MyJoinRequest[]);
      setDiscoveredOrganizations((discoverResponse.data ?? []) as DiscoverOrganizationItem[]);

      if (forcePreView && myOrganizations.length === 1) {
        const singleOrganization = myOrganizations[0];
        const normalizedRoles = (singleOrganization.roles ?? [])
          .map(role => String(role || '').trim().toLowerCase())
          .filter(Boolean);

        if (normalizedRoles.length === 1 && normalizedRoles[0] === 'student') {
          setOrganizationContext({
            organizationId: singleOrganization.organizationId,
            organizationSlug: singleOrganization.organizationSlug,
            organizationName: singleOrganization.organizationName,
          });
          navigate(`/${singleOrganization.organizationSlug}/analytics`, { replace: true });
          return;
        }
      }

      if (!forcePreView && myOrganizations.length === 1) {
        setOrganizationContext({
          organizationId: myOrganizations[0].organizationId,
          organizationSlug: myOrganizations[0].organizationSlug,
          organizationName: myOrganizations[0].organizationName,
        });
        setShowDashboard(true);
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to load organizations.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, forcePreView, withAuth]);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    loadContextData();
  }, [isAuthenticated, loadContextData]);

  const searchOrganizations = useCallback(async () => {
    try {
      setSearching(true);
      setError('');

      const response = await withAuth({
        method: 'get',
        url: `${apiBaseUrl}/discover`,
        params: discoverQuery.trim() ? { query: discoverQuery.trim() } : undefined
      });

      setDiscoveredOrganizations((response.data ?? []) as DiscoverOrganizationItem[]);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to search organizations.';
      setError(message);
      setDiscoveredOrganizations([]);
    } finally {
      setSearching(false);
    }
  }, [apiBaseUrl, discoverQuery, withAuth]);

  const submitJoinRequest = async () => {
    if (!selectedOrganizationId) {
      setError('Please select an organization to join.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setNotice('');

      await withAuth({
        method: 'post',
        url: `${apiBaseUrl}/join-requests`,
        data: {
          organizationId: selectedOrganizationId,
          requestedRoleKey,
          message: requestMessage.trim() || undefined,
        }
      });

      const selected = discoveredOrganizations.find(o => o.organizationId === selectedOrganizationId);
      const display = selected ? `${selected.name} (${selected.slug})` : `organization #${selectedOrganizationId}`;

      setNotice(`Join request submitted for ${display}. Status: Pending approval.`);
      setRequestMessage('');
      await loadContextData();
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to submit join request.';
      setError(message);
      if ((message as string).toLowerCase().includes('already have a membership')) {
        await loadContextData();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleContinueWithOrganization = async (organizationId: number, target: 'dashboard' | 'admin' | 'teacher' = 'dashboard') => {
    try {
      setSaving(true);
      setError('');

      await withAuth({ method: 'post', url: `${apiBaseUrl}/${organizationId}/set-default` });
      const selected = organizations.find(o => o.organizationId === organizationId);
      if (selected) {
        setOrganizationContext({
          organizationId: selected.organizationId,
          organizationSlug: selected.organizationSlug,
          organizationName: selected.organizationName,
        });
        if (target === 'admin') {
          navigate('/admin', { replace: true });
          return;
        }
        if (target === 'teacher') {
          navigate('/teacher-dashboard', { replace: true });
          return;
        }
        navigate(`/${selected.organizationSlug}/analytics`, { replace: true });
        return;
      }

      setShowDashboard(true);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to set organization context.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleContinueToReview = () => {
    const trimmedName = newOrganizationName.trim();
    const trimmedSlug = newOrganizationSlug.trim();

    if (!trimmedName) {
      setError('Please enter an organization name.');
      return;
    }

    const nextSlug = trimmedSlug || slugify(trimmedName);
    if (!trimmedSlug) {
      setNewOrganizationSlug(nextSlug);
    }

    setError('');
    setCreateStep('review');
  };

  const handleCreateOrganizationWithTrial = async () => {
    const trimmedName = newOrganizationName.trim();
    const trimmedSlug = (newOrganizationSlug || slugify(newOrganizationName)).trim();
    const trimmedDescription = buildCreateDescription();

    if (!trimmedName || !trimmedSlug) {
      setError('Please enter both the organization name and slug.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setNotice('');

      const response = await withAuth({
        method: 'post',
        url: `${apiBaseUrl}`,
        data: {
          name: trimmedName,
          slug: trimmedSlug,
          description: trimmedDescription || undefined,
          plan: 'trial-3-months',
        }
      });

      const createdOrganization = response.data ?? {};
      const createdOrganizationId = Number(createdOrganization.id || createdOrganization.organizationId);
      const createdSlug = createdOrganization.slug || trimmedSlug;

      if (!createdOrganizationId) {
        throw new Error('Organization was created but no identifier was returned.');
      }

      await withAuth({ method: 'post', url: `${apiBaseUrl}/${createdOrganizationId}/set-default` });

      setOrganizationContext({
        organizationId: createdOrganizationId,
        organizationSlug: createdSlug,
        organizationName: trimmedName,
      });

      try {
        window.sessionStorage.setItem(
          'exammasters:org-onboarding:just-created',
          JSON.stringify({ organizationId: createdOrganizationId, organizationName: trimmedName, organizationSlug: createdSlug })
        );
      } catch {
        // Ignore storage issues in non-browser environments.
      }

      resetCreateModal();
      setNotice(`Organization created successfully. You now have a 3-month free trial and can invite users immediately.`);
      navigate('/admin', { replace: true });
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to create organization.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (showDashboard) {
    return <Dashboard />;
  }

  if (!isAuthenticated) {
    return (
      <div style={{ maxWidth: 760, margin: '30px auto', padding: '0 16px 24px' }}>
        <h2 style={{ marginBottom: 8 }}>Welcome</h2>
        <p style={{ color: '#4b5563', marginBottom: 16 }}>Please sign in to continue.</p>
        <button
          onClick={() => loginWithRedirect()}
          style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 16px', cursor: 'pointer', fontWeight: 600 }}
        >
          Sign In
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ color: '#4b5563' }}>Loading organizations...</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 960, margin: '30px auto', padding: '0 16px 30px' }}>
      <h2 style={{ marginBottom: 8 }}>Organization Access</h2>

      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 6, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {notice && (
        <div style={{ background: '#dcfce7', color: '#166534', padding: 12, borderRadius: 6, marginBottom: 12 }}>
          {notice}
        </div>
      )}

      {myJoinRequests.length > 0 && (
        <section style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: 16, marginBottom: 16 }}>
          <h3 style={{ marginTop: 0, marginBottom: 12 }}>Pending Join Requests</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            {myJoinRequests.map(request => (
              <div key={request.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                <div style={{ fontWeight: 700 }}>{request.organizationName} ({request.organizationSlug})</div>
                <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>
                  Requested role: {request.requestedRoleKey} | Status: {request.status} | Sent: {new Date(request.createdAt).toLocaleString()}
                </div>
                {request.message && (
                  <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>Note: {request.message}</div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {organizations.length === 0 && (
        <section style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: 16 }}>
          <h3 style={{ marginTop: 0, marginBottom: 10 }}>No organization found</h3>
          <p style={{ color: '#6b7280', marginTop: 0 }}>
            You are not a member of any organization yet. Use the options below to create one or request to join.
          </p>
          <p style={{ marginTop: 12, color: '#6b7280', fontSize: 13 }}>
            Start with a 3-month free trial and invite your team immediately.
          </p>
        </section>
      )}

      {organizations.length > 0 && (
        <section style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: 16 }}>
          <h3 style={{ marginTop: 0, marginBottom: 12 }}>Choose organization context</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            {organizations.map(org => (
              <div key={org.membershipId} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 700 }}>{org.organizationName} ({org.organizationSlug})</div>
                  <div style={{ color: '#6b7280', fontSize: 13 }}>
                    Roles: {org.roles.join(', ') || 'None'} {org.isDefault ? '| Default' : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {org.roles.some(role => role.trim().toLowerCase() === 'teacher') && (
                    <button
                      onClick={() => handleContinueWithOrganization(org.organizationId, 'teacher')}
                      disabled={saving}
                      style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 12px', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, fontWeight: 600 }}
                    >
                      Go to Teacher Admin
                    </button>
                  )}
                  {hasAdminRole(org.roles) && (
                    <button
                      onClick={() => handleContinueWithOrganization(org.organizationId, 'admin')}
                      disabled={saving}
                      style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 12px', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, fontWeight: 600 }}
                    >
                      Go to Admin
                    </button>
                  )}
                  <button
                    onClick={() => handleContinueWithOrganization(org.organizationId)}
                    disabled={saving}
                    style={{ background: '#111827', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 12px', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, fontWeight: 600 }}
                  >
                    Continue
                  </button>
                </div>
              </div>
            ))}
          </div>
          {defaultOrganizationId && (
            <p style={{ marginTop: 10, color: '#6b7280', fontSize: 13 }}>
              Current default organization id: {defaultOrganizationId}
            </p>
          )}
        </section>
      )}

      <p style={{ color: '#4b5563', marginTop: 20, marginBottom: 12 }}>
        Choose how you want to continue.
      </p>

      <section style={{ border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', padding: 18, marginBottom: 16 }}>
        <h3 style={{ marginTop: 0, marginBottom: 8 }}>Get Started</h3>
        <p style={{ color: '#6b7280', marginTop: 0, marginBottom: 14 }}>
          Start your own tutoring organization or request access to an existing one.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, background: '#f8fafc' }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Create Organization</div>
            <p style={{ color: '#6b7280', marginTop: 0, marginBottom: 12, fontSize: 14 }}>
              Set up your own organization and invite your team.
            </p>
            <button
              type="button"
              onClick={() => {
                setError('');
                setNotice('');
                setShowCreateModal(true);
                setCreateStep('form');
              }}
              style={{ display: 'inline-block', textDecoration: 'none', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 6, padding: '9px 12px', fontWeight: 600, cursor: 'pointer' }}
            >
              Create Organization
            </button>
          </div>

          <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, background: '#f8fafc' }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Join Organization</div>
            <p style={{ color: '#6b7280', marginTop: 0, marginBottom: 12, fontSize: 14 }}>
              Search for an organization and send a join request to the owner.
            </p>
            <button
              onClick={() => setShowJoinPanel(prev => !prev)}
              style={{ display: 'inline-block', textDecoration: 'none', background: '#0f766e', color: '#fff', border: 'none', borderRadius: 6, padding: '9px 12px', fontWeight: 600, cursor: 'pointer' }}
            >
              {showJoinPanel ? 'Hide Join Flow' : 'Join Organization'}
            </button>
          </div>
        </div>

        {showCreateModal && (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}
            onClick={resetCreateModal}
          >
            <div
              style={{ width: '100%', maxWidth: 560, background: '#ffffff', borderRadius: 14, boxShadow: '0 24px 70px rgba(15, 23, 42, 0.28)', padding: 20 }}
              onClick={event => event.stopPropagation()}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                <div style={{ fontWeight: 800, fontSize: 18, color: '#111827' }}>
                  {createStep === 'form' ? 'Set up your workspace' : 'Review and launch'}
                </div>
                <button
                  type="button"
                  onClick={resetCreateModal}
                  style={{ border: '1px solid #d1d5db', background: '#fff', borderRadius: 6, padding: '8px 10px', cursor: 'pointer', fontWeight: 700, color: '#374151' }}
                >
                  Close
                </button>
              </div>

              {createStep === 'form' ? (
                <>
                  <p style={{ color: '#4b5563', marginTop: 0, marginBottom: 12, fontSize: 14 }}>
                    Add your workspace details below. We will turn this into your active organization and set up the free trial for you.
                  </p>
                  <div style={{ display: 'grid', gap: 10 }}>
                    <input
                      value={newOrganizationName}
                      onChange={event => {
                        const nextName = event.target.value;
                        setNewOrganizationName(nextName);
                        if (!newOrganizationSlug) {
                          setNewOrganizationSlug(slugify(nextName));
                        }
                      }}
                      placeholder="Organization name"
                      style={{ padding: 10, border: '1px solid #d1d5db', borderRadius: 6 }}
                    />
                    <input
                      value={newOrganizationSlug}
                      onChange={event => setNewOrganizationSlug(slugify(event.target.value))}
                      placeholder="Organization slug"
                      style={{ padding: 10, border: '1px solid #d1d5db', borderRadius: 6 }}
                    />
                    <select
                      value={newOrganizationType}
                      onChange={event => setNewOrganizationType(event.target.value)}
                      style={{ padding: 10, border: '1px solid #d1d5db', borderRadius: 6 }}
                    >
                      {ORGANIZATION_TYPE_OPTIONS.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    <input
                      value={newOrganizationFocus}
                      onChange={event => setNewOrganizationFocus(event.target.value)}
                      placeholder="Primary focus (optional)"
                      style={{ padding: 10, border: '1px solid #d1d5db', borderRadius: 6 }}
                    />
                    <select
                      value={newOrganizationSize}
                      onChange={event => setNewOrganizationSize(event.target.value)}
                      style={{ padding: 10, border: '1px solid #d1d5db', borderRadius: 6 }}
                    >
                      {TEAM_SIZE_OPTIONS.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    <textarea
                      value={newOrganizationDescription}
                      onChange={event => setNewOrganizationDescription(event.target.value)}
                      placeholder="Short description (optional)"
                      rows={3}
                      style={{ padding: 10, border: '1px solid #d1d5db', borderRadius: 6, resize: 'vertical' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={handleContinueToReview}
                      disabled={saving}
                      style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '9px 12px', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}
                    >
                      Continue to Review
                    </button>
                    <button
                      type="button"
                      onClick={resetCreateModal}
                      style={{ background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, padding: '9px 12px', fontWeight: 600, cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p style={{ color: '#4b5563', marginTop: 0, marginBottom: 12, fontSize: 14 }}>
                    Review your workspace details below, then launch your free trial and head straight into the admin dashboard.
                  </p>
                  <div style={{ border: '1px solid #dbeafe', borderRadius: 10, background: '#f8fbff', padding: 12, marginBottom: 12, display: 'grid', gap: 8 }}>
                    <div style={{ fontSize: 13, color: '#4b5563' }}>Workspace summary</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>{newOrganizationName.trim() || 'Your organization'}</div>
                    <div style={{ color: '#4b5563', fontSize: 13 }}>Slug: {newOrganizationSlug.trim() || slugify(newOrganizationName)}</div>
                    <div style={{ color: '#4b5563', fontSize: 13 }}>Type: {newOrganizationType}</div>
                    {newOrganizationFocus.trim() ? <div style={{ color: '#4b5563', fontSize: 13 }}>Focus: {newOrganizationFocus.trim()}</div> : null}
                    <div style={{ color: '#4b5563', fontSize: 13 }}>Size: {newOrganizationSize}</div>
                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>3-month free trial • no payment required today</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={handleCreateOrganizationWithTrial}
                      disabled={saving}
                      style={{ background: '#0f766e', color: '#fff', border: 'none', borderRadius: 6, padding: '9px 12px', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}
                    >
                      {saving ? 'Creating...' : 'Try free for 3 months'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setCreateStep('form')}
                      style={{ background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, padding: '9px 12px', fontWeight: 600, cursor: 'pointer' }}
                    >
                      Back
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {showJoinPanel && (
          <div style={{ marginTop: 14, borderTop: '1px solid #e5e7eb', paddingTop: 14 }}>
            <h4 style={{ marginTop: 0, marginBottom: 10 }}>Join Flow</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, marginBottom: 10 }}>
              <input
                value={discoverQuery}
                onChange={e => setDiscoverQuery(e.target.value)}
                placeholder="Search by organization name or slug"
                style={{ padding: 10, border: '1px solid #d1d5db', borderRadius: 6 }}
              />
              <button
                onClick={searchOrganizations}
                disabled={searching}
                style={{ background: '#374151', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 12px', cursor: searching ? 'not-allowed' : 'pointer', opacity: searching ? 0.7 : 1 }}
              >
                {searching ? 'Searching...' : 'Search'}
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: 8, marginBottom: 10 }}>
              <select
                value={selectedOrganizationId}
                onChange={e => setSelectedOrganizationId(Number(e.target.value))}
                style={{ padding: 10, border: '1px solid #d1d5db', borderRadius: 6 }}
              >
                <option value={0}>Select organization</option>
                {discoveredOrganizations.map(org => (
                  <option key={org.organizationId} value={org.organizationId}>{org.name} ({org.slug})</option>
                ))}
              </select>

              <select
                value={requestedRoleKey}
                onChange={e => setRequestedRoleKey(e.target.value)}
                style={{ padding: 10, border: '1px solid #d1d5db', borderRadius: 6 }}
              >
                {ROLE_OPTIONS.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>

            <textarea
              value={requestMessage}
              onChange={e => setRequestMessage(e.target.value)}
              placeholder="Optional note to organization owner"
              rows={3}
              style={{ width: '100%', marginBottom: 10, padding: 10, border: '1px solid #d1d5db', borderRadius: 6, resize: 'vertical', boxSizing: 'border-box' }}
            />

            <button
              onClick={submitJoinRequest}
              disabled={saving}
              style={{ background: '#0f766e', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 14px', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, fontWeight: 600 }}
            >
              {saving ? 'Submitting...' : 'Submit Join Request'}
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default OrganizationPreDashboard;
